import { clsx } from 'clsx'
import { CornerDownLeft, Moon, RefreshCw, Search, Sun, SunMoon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { NavItem } from './nav'
import { useTheme } from '@/hooks/useTheme'

interface Command {
  id: string
  label: string
  hint?: string
  group: string
  keywords?: string
  run: () => void
  icon?: React.ReactNode
}

const HAS_JAPANESE = /[\u3040-\u30ff\u3400-\u9fff]/

/**
 * Ranking for the palette.
 * Latin queries allow a gap-tolerant match ("dashb" finds dashboard); Japanese
 * queries require a literal run, because kana spread across a keyword list
 * matches almost anything by accident.
 */
function score(haystack: string, needle: string): number {
  if (!needle) return 1
  const h = haystack.toLocaleLowerCase('ja-JP')
  const n = needle.toLocaleLowerCase('ja-JP')

  const direct = h.indexOf(n)
  if (direct === 0) return 1000
  if (direct > 0) return 700 - Math.min(direct, 600)
  if (HAS_JAPANESE.test(n)) return 0

  let hi = 0
  let streak = 0
  let best = 0
  for (const ch of n) {
    const idx = h.indexOf(ch, hi)
    if (idx === -1) return 0
    streak = idx === hi ? streak + 1 : 1
    best = Math.max(best, streak)
    hi = idx + 1
  }
  return 100 + best * 10 - Math.min(hi, 90)
}

export function CommandPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: NavItem[] }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { choice, setChoice } = useTheme()
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  const commands = useMemo<Command[]>(() => {
    const pages: Command[] = items.map((item) => ({
      id: `nav:${item.to}`,
      label: item.label,
      hint: item.description,
      group: '画面',
      keywords: `${item.to} ${item.keywords ?? ''}`,
      icon: <item.icon className="h-4 w-4" />,
      run: () => navigate(item.to),
    }))

    const actions: Command[] = [
      {
        id: 'act:refresh',
        label: 'データを再取得',
        hint: '表示中の画面のキャッシュを破棄して読み直す',
        group: '操作',
        keywords: 'reload refresh 更新 リロード',
        icon: <RefreshCw className="h-4 w-4" />,
        run: () => void qc.invalidateQueries(),
      },
      {
        id: 'act:theme-light',
        label: 'ライトテーマにする',
        group: '外観',
        keywords: 'theme light 明るい',
        icon: <Sun className="h-4 w-4" />,
        run: () => setChoice('light'),
      },
      {
        id: 'act:theme-dark',
        label: 'ダークテーマにする',
        group: '外観',
        keywords: 'theme dark 暗い 夜',
        icon: <Moon className="h-4 w-4" />,
        run: () => setChoice('dark'),
      },
      {
        id: 'act:theme-system',
        label: 'テーマをOSに合わせる',
        hint: `現在: ${choice === 'system' ? 'OSに追従' : choice === 'dark' ? 'ダーク' : 'ライト'}`,
        group: '外観',
        keywords: 'theme system auto OS',
        icon: <SunMoon className="h-4 w-4" />,
        run: () => setChoice('system'),
      },
      {
        id: 'act:copy-link',
        label: 'この画面のリンクをコピー',
        hint: '絞り込み条件を含めた共有用URL',
        group: '操作',
        keywords: 'copy url link 共有 リンク',
        icon: <CornerDownLeft className="h-4 w-4" />,
        run: () => void navigator.clipboard?.writeText(window.location.href),
      },
    ]

    return [...pages, ...actions]
  }, [navigate, qc, setChoice, choice, items])

  const results = useMemo(() => {
    const term = q.trim()
    const scored = commands
      .map((c) => ({ c, s: Math.max(score(c.label, term), score(c.keywords ?? '', term) * 0.8) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
    return scored.slice(0, 40).map((r) => r.c)
  }, [commands, q])

  useEffect(() => setActive(0), [q])

  useEffect(() => {
    if (!open) return
    setQ('')
    setActive(0)
  }, [open])

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (!open) return null

  // Group by name rather than by adjacency: results are score-ordered, so the
  // same group can otherwise appear twice and collide on its React key.
  const groupOrder: string[] = []
  const byGroup = new Map<string, Command[]>()
  for (const c of results) {
    if (!byGroup.has(c.group)) {
      byGroup.set(c.group, [])
      groupOrder.push(c.group)
    }
    byGroup.get(c.group)!.push(c)
  }
  const grouped: Array<[string, Command[]]> = groupOrder.map((g) => [g, byGroup.get(g)!])
  let flatIndex = -1

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 pt-[12vh]">
      <button type="button" aria-label="閉じる" onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="コマンドパレット"
        className="relative flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-pop animate-slide-up"
      >
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((i) => Math.min(i + 1, results.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((i) => Math.max(i - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const pick = results[active]
                if (pick) {
                  pick.run()
                  onClose()
                }
              } else if (e.key === 'Escape') {
                onClose()
              }
            }}
            placeholder="画面や操作を検索"
            className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
          />
          <kbd className="hidden rounded border border-line bg-surface-2 px-1.5 py-0.5 text-2xs text-ink-3 sm:block">
            Esc
          </kbd>
        </div>

        <ul ref={listRef} className="min-h-0 flex-1 overflow-y-auto py-1.5">
          {results.length === 0 ? (
            <li className="px-4 py-10 text-center text-sm text-ink-3">一致する項目がありません</li>
          ) : (
            grouped.map(([group, items]) => (
              <li key={group}>
                <p className="px-4 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-ink-3">
                  {group}
                </p>
                <ul>
                  {items.map((c) => {
                    flatIndex += 1
                    const idx = flatIndex
                    const on = idx === active
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          data-active={on}
                          onMouseEnter={() => setActive(idx)}
                          onClick={() => {
                            c.run()
                            onClose()
                          }}
                          className={clsx(
                            'flex w-full items-center gap-3 px-4 py-2 text-left',
                            on ? 'bg-accent/10 text-ink' : 'text-ink-2 hover:bg-surface-hover',
                          )}
                        >
                          <span className={clsx('shrink-0', on ? 'text-accent' : 'text-ink-3')}>{c.icon}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{c.label}</span>
                            {c.hint ? <span className="block truncate text-2xs text-ink-3">{c.hint}</span> : null}
                          </span>
                          {on ? <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden /> : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>,
    document.body,
  )
}
